'use client';

import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetch error', {
      url,
      payloadStr,
      status: err?.response?.status,
      responseData: err?.response?.data,
      message: err?.message
    });
    throw err;
  }
};

export const revalidateRoleList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/roles/search');
};

export const buildRoleSearchPayload = (page, rowsPerPage, searchField, searchValue) => {
  let roleName = '';
  if (typeof window !== 'undefined') {
    roleName = localStorage.getItem('rolename') || localStorage.getItem('role_name') || '';
  }

  const fb = new FilterBuilder().setPaging(page * rowsPerPage, rowsPerPage).setOrder(['role_name'], true);

  if (roleName !== 'Administrator') {
    fb.equal('role_parent', roleName, 'OR');
    fb.equal('role_name', roleName, 'OR');
  }

  if (searchValue && searchValue.trim() !== '') {
    const val = searchValue.trim();
    if (searchField === 'role_name') {
      fb.addFilter('role_name', val);
    } else if (searchField === 'package_name') {
      fb.addFilter('package_name', val);
    } else {
      // default 'all'
      fb.addFilter('role_name', val);
      fb.addFilter('package_name', val);
    }
  }

  return fb.build();
};

export const useRoleList = (page = 0, rowsPerPage = 1000, searchField = 'all', searchValue = '') => {
  const payload = useMemo(
    () => buildRoleSearchPayload(page, rowsPerPage, searchField, searchValue),
    [page, rowsPerPage, searchField, searchValue]
  );

  const key = useMemo(() => ['/roles/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.data?.totalRecords ?? data?.data?.dataSize ?? data?.totalRecord ?? 0,
    isLoading,
    isError: error
  };
};

export const buildPackageSearchPayload = (page, rowsPerPage, searchField, searchValue) => {
  let filters = undefined;
  if (searchValue && searchValue.trim() !== '') {
    const val = searchValue.trim();
    if (searchField === 'package_name') {
      filters = [{ name: 'package_name', value: val }];
    } else {
      filters = [{ name: 'package_name', value: val }];
    }
  }

  const fb = new FilterBuilder().setPaging(page * rowsPerPage, rowsPerPage).setOrder(['id'], false);

  if (filters && filters.length > 0) {
    filters.forEach((f) => {
      fb.like(f.name, f.value);
    });
  }

  return fb.build();
};

export const usePackageList = (page = 0, rowsPerPage = 10, searchField = 'package_name', searchValue = '') => {
  const payload = useMemo(
    () => buildPackageSearchPayload(page, rowsPerPage, searchField, searchValue),
    [page, rowsPerPage, searchField, searchValue]
  );

  const key = useMemo(() => ['/packages/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.data?.totalRecords ?? data?.data?.dataSize ?? data?.totalRecord ?? 0,
    isLoading,
    isError: error
  };
};

export const revalidatePackageList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/packages/search');
};

export const getPackageMenuList = async (start, length) => {
  try {
    const response = await fleetApi.get(`/packages/list${getTzParam()}&start=${start}&length=${length}`);
    return response;
  } catch (error) {
    console.error('Failed to fetch package menu list:', error?.response?.data ?? error);
    throw error;
  }
};

export const getRoleById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/roles/search${getTzParam()}`, payload);

    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch role by ID:', error);
    throw error;
  }
};

export const createRole = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/roles/new${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRoleList();
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateRole = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/roles/edit/${id}${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRoleList();
    return response.data;
  } catch (error) {
    console.error('Error updating role:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteRole = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/roles/delete/${id}${getTzParam()}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateRoleList();
    return response.data;
  } catch (error) {
    console.error('Error deleting role:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateVehicleForRole = async (id, payload) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/roles-vehicle/edit/${id}${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Error updating vehicle for role:', error?.response?.data ?? error);
    throw error;
  }
};
export const createCompanyForRole = async (payload) => {
  try {
    const response = await fleetApi.post(`/roles-company/new${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Error updating company for role:', error?.response?.data ?? error);
    throw error;
  }
};

export const createVehicleForRole = async (payload) => {
  try {
    const response = await fleetApi.post(`/roles-vehicle/new${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Error updating vehicle for role:', error?.response?.data ?? error);
    throw error;
  }
};
export const updateCompanyForRole = async (id, payload) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/roles-company/edit/${id}${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Error updating company for role:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateAllMenuForRole = async (payload) => {
  try {
    const response = await fleetApi.put(`/roles-module/edit-all${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Error updating menu for role:', error?.response?.data ?? error);
    throw error;
  }
};

export const createPackageMenu = async (payload) => {
  try {
    const response = await fleetApi.post('/packages/new', payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Failed to create Package menu:', error?.response?.data ?? error);
    throw error;
  }
};

export const updatePackageMenu = async (payload) => {
  if (!payload?.id) throw new Error('Package ID is required in payload for update');
  try {
    const response = await fleetApi.put(`/packages/edit/${payload.id}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error(`Failed to update package menu ${payload.id}:`, error?.response?.data ?? error);
    throw error;
  }
};

export const deletePackageMenu = async (id) => {
  if (!id) throw new Error('Package ID is required for deletion');
  try {
    const response = await fleetApi.delete(`/packages/delete/${id}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error(`Failed to delete package menu ${id}:`, error?.response?.data ?? error);
    throw error;
  }
};
export const getPackageModuleByName = async (packageName) => {
  if (!packageName) throw new Error('packageName is required');
  try {
    const response = await fleetApi.get(`/package-module-by-name?packageName=${packageName}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch package module for ${packageName}:`, error?.response?.data ?? error);
    throw error;
  }
};
export const updatePackageMenuAll = async (payload) => {
  try {
    const response = await fleetApi.put('/package-module/edit-all', payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Failed to update package module all:', error?.response?.data ?? error);
    throw error;
  }
};
